import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Upload, Pencil } from "lucide-react";
import { toast } from "sonner";
import { uploadSiteRuleFile } from "@/lib/api";

/**
 * Type-specific form editors for site_rule_sections.
 * Falls back to a raw JSON textarea for unsupported types so admins
 * can always edit something.
 */
export default function SectionFormEditor({ section, onChange, onChangeBody, siteId }) {
  const t = section.section_type;
  if (t === "info_block")    return <InfoBlock data={section.data} onChange={onChange} />;
  if (t === "ppe_checklist") return <PpeChecklist data={section.data} onChange={onChange} />;
  if (t === "accordion")     return <Accordion data={section.data} onChange={onChange} />;
  if (t === "travel_times")  return <TravelTimes data={section.data} onChange={onChange} />;
  if (t === "approach")      return <Approach data={section.data} onChange={onChange} />;
  if (t === "park_up_steps") return <ParkUpSteps data={section.data} onChange={onChange} />;
  if (t === "file_upload")   return <FileUpload data={section.data} onChange={onChange} siteId={siteId} />;
  if (t === "site_map")      return <SiteMapEditor data={section.data} onChange={onChange} siteId={siteId} />;
  if (t === "markdown") {
    return (
      <Textarea
        rows={8}
        value={section.body_markdown || ""}
        onChange={(e) => onChangeBody(e.target.value)}
        data-testid="rules-form-markdown"
        placeholder="Write content in plain text or Markdown…"
      />
    );
  }
  return <JsonFallback data={section.data} onChange={onChange} />;
}

function InfoBlock({ data, onChange }) {
  const rows = data?.rows || [];
  const set = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...next[i], [k]: v };
    onChange({ rows: next });
  };
  return (
    <div className="space-y-2" data-testid="rules-form-info">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2">
          <Input value={r.label || ""} onChange={(e) => set(i, "label", e.target.value)} placeholder="Label" />
          <Input value={r.value || ""} onChange={(e) => set(i, "value", e.target.value)} placeholder="Value" />
          <Button size="icon" variant="ghost" onClick={() => onChange({ rows: rows.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ rows: [...rows, { label: "", value: "" }] })} data-testid="info-add-row">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add row
      </Button>
    </div>
  );
}

function PpeChecklist({ data, onChange }) {
  const items = data?.items || [];
  return (
    <div className="space-y-2" data-testid="rules-form-ppe">
      <p className="text-[11px] text-slate-500 italic">
        Click any item to edit its text. Use the × to remove.
      </p>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-center group">
          <div className="flex-1 relative">
            <Input value={it}
                   onChange={(e) => {
                     const next = items.slice(); next[i] = e.target.value;
                     onChange({ ...data, items: next });
                   }}
                   placeholder="e.g. Hi-vis vest"
                   className="pr-8 border-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
                   data-testid={`ppe-item-input-${i}`} />
            <Pencil className="h-3 w-3 text-slate-300 group-hover:text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-colors" />
          </div>
          <Button size="icon" variant="ghost"
                  onClick={() => onChange({ ...data, items: items.filter((_, j) => j !== i) })}
                  data-testid={`ppe-item-delete-${i}`}
                  title="Remove item">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ ...data, items: [...items, ""] })} data-testid="ppe-add-item">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add PPE item
      </Button>
      <Input placeholder="Other notes" value={data?.other || ""} onChange={(e) => onChange({ ...data, other: e.target.value })} />
    </div>
  );
}

function Accordion({ data, onChange }) {
  const items = data?.items || [];
  const set = (i, k, v) => {
    const next = items.slice(); next[i] = { ...next[i], [k]: v };
    onChange({ items: next });
  };
  return (
    <div className="space-y-3" data-testid="rules-form-accordion">
      {items.map((it, i) => (
        <div key={i} className="border rounded p-2 space-y-1.5">
          <div className="flex gap-2">
            <Input className="flex-1" placeholder="Title" value={it.title || ""} onChange={(e) => set(i, "title", e.target.value)} />
            <Button size="icon" variant="ghost" onClick={() => onChange({ items: items.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
          </div>
          <Textarea rows={3} placeholder="Body (Markdown)" value={it.body || ""} onChange={(e) => set(i, "body", e.target.value)} />
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ items: [...items, { title: "", body: "" }] })} data-testid="accordion-add-item">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add item
      </Button>
    </div>
  );
}

function TravelTimes({ data, onChange }) {
  const rows = data?.rows || [];
  const set = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...next[i], [k]: k === "minutes" ? (parseInt(v, 10) || 0) : v };
    onChange({ rows: next });
  };
  return (
    <div className="space-y-2" data-testid="rules-form-travel">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_100px_auto] gap-2">
          <Input value={r.origin || ""} placeholder="Origin" onChange={(e) => set(i, "origin", e.target.value)} />
          <Input value={r.route || ""}  placeholder="Route"  onChange={(e) => set(i, "route", e.target.value)} />
          <Input type="number" value={r.minutes || 0} onChange={(e) => set(i, "minutes", e.target.value)} />
          <Button size="icon" variant="ghost" onClick={() => onChange({ rows: rows.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ rows: [...rows, { origin: "", route: "", minutes: 0 }] })} data-testid="travel-add-row">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add origin
      </Button>
    </div>
  );
}

function Approach({ data, onChange }) {
  const routes = data?.routes || [];
  const setRoute = (i, k, v) => {
    const next = routes.slice();
    next[i] = { ...next[i], [k]: v };
    onChange({ routes: next });
  };
  const setStep = (ri, si, v) => {
    const steps = (routes[ri]?.steps || []).slice();
    steps[si] = v;
    setRoute(ri, "steps", steps);
  };
  return (
    <div className="space-y-3" data-testid="rules-form-approach">
      {routes.map((r, ri) => (
        <div key={ri} className="border rounded p-2 space-y-1.5">
          <div className="flex gap-2 items-center">
            <Input
              className="flex-1"
              placeholder="From (e.g. WEST, NORTH, EAST from Port Wakefield)"
              value={r.from || ""}
              onChange={(e) => setRoute(ri, "from", e.target.value)}
            />
            <Button size="icon" variant="ghost" onClick={() => onChange({ routes: routes.filter((_, j) => j !== ri) })}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-1">
            {(r.steps || []).map((s, si) => (
              <div key={si} className="flex gap-2">
                <span className="text-xs text-slate-400 font-mono pt-2 w-5 text-right">{si + 1}.</span>
                <Input value={s} placeholder="Step (e.g. TURN RIGHT — Fowler Terrace)" onChange={(e) => setStep(ri, si, e.target.value)} />
                <Button size="icon" variant="ghost" onClick={() => setRoute(ri, "steps", r.steps.filter((_, j) => j !== si))}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setRoute(ri, "steps", [...(r.steps || []), ""])}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add step
            </Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ routes: [...routes, { from: "", steps: [""] }] })} data-testid="approach-add-route">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add approach
      </Button>
    </div>
  );
}

function ParkUpSteps({ data, onChange }) {
  const steps = data?.steps || [];
  const set = (i, k, v) => {
    const next = steps.slice();
    next[i] = { ...next[i], [k]: v };
    onChange({ steps: next });
  };
  return (
    <div className="space-y-2" data-testid="rules-form-parkup">
      {steps.map((s, i) => (
        <div key={i} className="grid grid-cols-[120px_1fr_auto] gap-2">
          <Input value={s.action || ""} placeholder="ACTION (PARK/WALK/CHECK)" onChange={(e) => set(i, "action", e.target.value.toUpperCase())} />
          <Input value={s.description || ""} placeholder="Description" onChange={(e) => set(i, "description", e.target.value)} />
          <Button size="icon" variant="ghost" onClick={() => onChange({ steps: steps.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ steps: [...steps, { action: "", description: "" }] })} data-testid="parkup-add-step">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add step
      </Button>
    </div>
  );
}

function FileUpload({ data, onChange, siteId }) {
  const files = data?.files || [];
  const [busy, setBusy] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const { data: up, error } = await uploadSiteRuleFile(siteId, file);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    onChange({ files: [...files, { name: up.name, url: up.url, path: up.path }] });
    toast.success("File uploaded.");
  };
  return (
    <div className="space-y-2" data-testid="rules-form-files">
      {files.map((f, i) => (
        <div key={i} className="flex items-center gap-2 text-sm border rounded px-2 py-1.5">
          <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-600 underline flex-1 truncate">{f.name}</a>
          <Button size="icon" variant="ghost" onClick={() => onChange({ files: files.filter((_, j) => j !== i) })}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <label className="inline-flex">
        <input type="file" hidden onChange={handleUpload} disabled={busy} data-testid="files-upload-input" />
        <Button size="sm" variant="outline" asChild disabled={busy}>
          <span><Upload className="h-3.5 w-3.5 mr-1" /> {busy ? "Uploading…" : "Upload file"}</span>
        </Button>
      </label>
    </div>
  );
}

function SiteMapEditor({ data, onChange, siteId }) {
  const [busy, setBusy] = useState(false);
  const imageUrl = data?.image_url || "";
  const caption  = data?.caption || "";

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG / JPG / WebP).");
      return;
    }
    setBusy(true);
    const { data: up, error } = await uploadSiteRuleFile(siteId, file);
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    onChange({ image_url: up.url, path: up.path, caption });
    toast.success("Site map uploaded.");
  };

  return (
    <div className="space-y-2" data-testid="rules-form-site-map">
      {imageUrl ? (
        <div className="rounded border bg-slate-50 p-2 space-y-2">
          <img
            src={imageUrl}
            alt="Site map preview"
            className="max-h-72 w-full object-contain rounded"
            data-testid="site-map-preview"
          />
          <Button
            size="sm" variant="ghost"
            onClick={() => onChange({ image_url: "", caption })}
            data-testid="site-map-remove"
          >
            <X className="h-4 w-4 mr-1" /> Remove image
          </Button>
        </div>
      ) : (
        <label className="inline-flex">
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleUpload}
            disabled={busy}
            data-testid="site-map-upload-input"
          />
          <Button size="sm" variant="outline" asChild disabled={busy}>
            <span><Upload className="h-3.5 w-3.5 mr-1" /> {busy ? "Uploading…" : "Upload site map image"}</span>
          </Button>
        </label>
      )}
      <Input
        placeholder="Optional caption (e.g. 'Bay numbering — approach via McCormick Rd')"
        value={caption}
        onChange={(e) => onChange({ image_url: imageUrl, caption: e.target.value })}
        data-testid="site-map-caption"
      />
    </div>
  );
}

function JsonFallback({ data, onChange }) {
  const [draft, setDraft] = useState(JSON.stringify(data || {}, null, 2));
  const [err, setErr] = useState(null);
  return (
    <div className="space-y-1.5">
      <Textarea rows={8} className="font-mono text-xs" value={draft} onChange={(e) => {
        setDraft(e.target.value);
        try { onChange(JSON.parse(e.target.value || "{}")); setErr(null); }
        catch (x) { setErr(x.message); }
      }} />
      {err && <p className="text-xs text-rose-600">JSON error: {err}</p>}
    </div>
  );
}
